import React from 'react';
import { Box, Typography, Paper, LinearProgress, Stack } from '@mui/material';
import { ChartData } from '../../types';
import { useChartTheme } from './ChartTheme';

interface SimpleChartProps {
  data: ChartData;
  title?: string;
  height?: number;
  className?: string;
}

// Export the new SimplePieChart from its own file
export { SimplePieChart } from './SimplePieChart';

export const SimpleBarChart: React.FC<
  SimpleChartProps & { orientation?: 'vertical' | 'horizontal' }
> = ({ data, title, height = 300, orientation = 'vertical', className }) => {
  const chartTheme = useChartTheme();
  const colors = chartTheme.colors.default;

  const dataset = data.datasets[0];
  if (!dataset) return null;

  const maxValue = Math.max(...dataset.data);
  const chartData = data.labels.map((label, index) => ({
    label,
    value: dataset.data[index] || 0,
    percentage: maxValue > 0 ? ((dataset.data[index] || 0) / maxValue) * 100 : 0,
    color: dataset.backgroundColor?.[index] || colors[index % colors.length],
  }));

  return (
    <Paper className={className} sx={{ p: 2, height }}>
      {title && (
        <Typography variant="h6" gutterBottom textAlign="center">
          {title}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Simple bar representation */}
        <Stack spacing={2}>
          {chartData.map((item, index) => (
            <Box key={index}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {item.value}
                </Typography>
              </Box>

              {orientation === 'vertical' ? (
                <Box
                  sx={{
                    width: '100%',
                    height: 30,
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${item.percentage}%`,
                      height: '100%',
                      backgroundColor: item.color,
                      borderRadius: 1,
                      transition: 'width 0.3s ease-in-out',
                    }}
                  />
                </Box>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={item.percentage}
                  sx={{
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: item.color,
                      borderRadius: 4,
                    },
                  }}
                />
              )}
            </Box>
          ))}
        </Stack>

        {/* Summary */}
        <Box mt={2} p={2} sx={{ backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            <strong>Highest Response:</strong> {Math.max(...dataset.data)} •
            <strong> Total Categories:</strong> {chartData.length}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
